import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

interface BlocksState {
    // Id of the user the list belongs to, so a list is never applied to another account
    ownerUserId: string | null;
    blockedUserIds: string[];
}

const initialState: BlocksState = {
    ownerUserId: null,
    blockedUserIds: [],
}

const blocksSlice = createSlice({
    name: 'blocks',
    initialState,
    reducers: {
        setBlockedUsers: (state, action: PayloadAction<{ ownerUserId: string, blockedUserIds: string[] }>) => {
            state.ownerUserId = action.payload.ownerUserId;
            state.blockedUserIds = action.payload.blockedUserIds;
        },
        addBlockedUser: (state, action: PayloadAction<{ ownerUserId: string, blockedUserId: string }>) => {
            if(state.ownerUserId !== action.payload.ownerUserId) {
                state.ownerUserId = action.payload.ownerUserId;
                state.blockedUserIds = [];
            }
            if(!state.blockedUserIds.includes(action.payload.blockedUserId)) {
                state.blockedUserIds.push(action.payload.blockedUserId);
            }
        },
        removeBlockedUser: (state, action: PayloadAction<{ ownerUserId: string, blockedUserId: string }>) => {
            if(state.ownerUserId === action.payload.ownerUserId) {
                state.blockedUserIds = state.blockedUserIds.filter(id => id !== action.payload.blockedUserId);
            }
        },
        clearBlockedUsers: () => initialState,
    }
});

const EMPTY_SET: ReadonlySet<string> = new Set();

export const selectBlockedUserIds = createSelector(
    [
        (state: { blocks: BlocksState, auth: { currentUserId: string | null } }) => state.blocks.ownerUserId,
        (state: { blocks: BlocksState, auth: { currentUserId: string | null } }) => state.blocks.blockedUserIds,
        (state: { blocks: BlocksState, auth: { currentUserId: string | null } }) => state.auth.currentUserId,
    ],
    (ownerUserId, blockedUserIds, currentUserId): ReadonlySet<string> =>
        ownerUserId && ownerUserId === currentUserId ? new Set(blockedUserIds) : EMPTY_SET
);

export const { setBlockedUsers, addBlockedUser, removeBlockedUser, clearBlockedUsers } = blocksSlice.actions;
export default blocksSlice.reducer;
